import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ordenesApi, reportesApi, cajaApi } from '../services/api';
import { useSucursal } from '../context/SucursalContext';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateHelpers';

const Dashboard = () => {
  const { sucursales, sucursalActual, getSucursalId, getSucursalNombre } = useSucursal();
  const { isAdmin, isRecepcionista, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cajasAutoAbiertas, setCajasAutoAbiertas] = useState([]);

  useEffect(() => {
    loadData();
  }, [getSucursalId()]);

  useEffect(() => {
    if (!isAdmin() && !isRecepcionista()) return;

    const tiendasAChequear = sucursalActual
      ? [sucursalActual]
      : (isAdmin() ? sucursales : (user?.tienda_id ? [{ id: user.tienda_id, nombre: getSucursalNombre() }] : []));

    if (tiendasAChequear.length === 0) return;

    let cancelado = false;
    Promise.all(
      tiendasAChequear.map(t =>
        cajaApi.getEstado(t.id)
          .then(data => (data.abierta && data.caja?.observaciones?.includes('Apertura automática')) ? { id: t.id, nombre: t.nombre } : null)
          .catch(() => null)
      )
    ).then(resultados => {
      if (!cancelado) setCajasAutoAbiertas(resultados.filter(Boolean));
    });

    return () => { cancelado = true; };
  }, [sucursales, sucursalActual, isAdmin, isRecepcionista, user, getSucursalNombre]);

  const loadData = async () => {
    setLoading(true);
    try {
      const tienda_id = getSucursalId();
      const params = tienda_id ? { tienda_id } : {};

      // Si es técnico, solo mostrar sus órdenes asignadas
      if (user && user.rol === 'tecnico') {
        params.tecnico_id = user.id;
      }

      const [statsRes, ordersRes] = await Promise.all([
        reportesApi.getResumen(params),
        ordenesApi.getAll({ ...params, limit: 5 })
      ]);
      setStats(statsRes);
      setRecentOrders(ordersRes.data || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusCount = (status) => {
    if (!stats?.por_status) return 0;
    const found = stats.por_status.find(s => s.status === status);
    return found ? parseInt(found.cantidad) : 0;
  };

  const formatMoney = (amount) => {
    return `Q${parseFloat(amount || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;
  };


  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">{getSucursalNombre()}</p>
        </div>
        <Link to="/ordenes/nueva" className="btn btn-primary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Nueva Orden
        </Link>
      </div>

      {cajasAutoAbiertas.length > 0 && (
        <div style={{
          background: '#fff7ed',
          border: '1px solid #fdba74',
          borderRadius: '8px',
          padding: '14px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          fontSize: '13px',
          color: '#9a3412'
        }}>
          <span>
            <strong>⚠️ Caja abierta automáticamente:</strong> {cajasAutoAbiertas.map(c => c.nombre).join(', ')} tiene{cajasAutoAbiertas.length > 1 ? 'n' : ''} una caja que se abrió sola al registrar un cobro, sin que nadie la abriera a mano. Revísala y ciérrala con el monto real contado.
          </span>
          <Link to="/caja" className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }}>Ir a Caja Diaria</Link>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon orange">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <div className="stat-info">
            <h3>{getStatusCount('pendiente')}</h3>
            <p>Pendientes</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon blue">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
            </svg>
          </div>
          <div className="stat-info">
            <h3>{getStatusCount('proceso')}</h3>
            <p>En Proceso</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <div className="stat-info">
            <h3>{getStatusCount('terminada')}</h3>
            <p>Terminadas</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon red">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="3" width="15" height="13"/>
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
              <circle cx="5.5" cy="18.5" r="2.5"/>
              <circle cx="18.5" cy="18.5" r="2.5"/>
            </svg>
          </div>
          <div className="stat-info">
            <h3>{getStatusCount('entregada')}</h3>
            <p>Entregadas</p>
          </div>
        </div>
      </div>

      {/* Reportes financieros - Solo visibles para administrador */}
      {isAdmin() && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon blue">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <div className="stat-info">
              <h3>{stats?.totales?.total_ordenes || 0}</h3>
              <p>Total Órdenes</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon green">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
            <div className="stat-info">
              <h3>{formatMoney(stats?.mes?.monto)}</h3>
              <p>Ingresos del Mes ({stats?.mes?.cantidad || 0} órdenes)</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon orange">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
            <div className="stat-info">
              <h3>{formatMoney(stats?.totales?.total_pendiente)}</h3>
              <p>Pendiente de Cobro</p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Órdenes Recientes</h2>
          <Link to="/ordenes" className="btn btn-secondary btn-sm">Ver Todas</Link>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>N° Orden</th>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Equipo</th>
                <th>Status</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                    No hay órdenes registradas
                  </td>
                </tr>
              ) : (
                recentOrders.map(orden => (
                  <tr key={orden.id}>
                    <td>
                      <Link to={`/ordenes/${orden.id}`} style={{ fontWeight: 600 }}>
                        #{orden.numero_orden}
                      </Link>
                    </td>
                    <td>{formatDate(orden.fecha_recepcion)}</td>
                    <td>{orden.nombre_cliente}</td>
                    <td>{orden.equipo1_descripcion || '-'}</td>
                    <td>
                      <span className={`status-badge status-${orden.status}`}>
                        {orden.status}
                      </span>
                    </td>
                    <td>{formatMoney(orden.valor_total)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
